// components/EmployeeManagement.js
import React, { useState, useEffect, useContext } from 'react';
import { supabase } from '../supabaseClient';
import { AuthContext } from './AuthContext';
import SidebarNav from "./SidebarNav";

const EmployeeManagement = () => {
    const [employees, setEmployees] = useState([]);
    const [editingEmployeeId, setEditingEmployeeId] = useState(null);
    const [employeeForm, setEmployeeForm] = useState({
        name: '',
        email: '',
        role: '',
        salary: '',
        contact: '',
        hire_date: '',
        employee_number: ''
    });
    const [message, setMessage] = useState('');

    const { userRole } = useContext(AuthContext);

    useEffect(() => {
        fetchEmployees();
    }, []);

    const fetchEmployees = async () => {
        const { data, error } = await supabase.from('employees').select('*');
        if (!error) {
            setEmployees(data);
        } else {
            console.error('Error fetching employees:', error);
            setMessage('无法获取员工信息');
        }
    };

    const handleEditEmployee = (employee) => {
        if (userRole !== 'admin') {
            setMessage('您没有权限编辑员工信息。');
            return;
        }
        setEditingEmployeeId(employee.id);
        setEmployeeForm({
            name: employee.name,
            email: employee.email,
            role: employee.role,
            salary: employee.salary,
            contact: employee.contact,
            hire_date: employee.hire_date ? employee.hire_date.slice(0, 10) : '',
            employee_number: employee.employee_number
        });
    };

    const handleCancelEdit = () => {
        setEditingEmployeeId(null);
        setEmployeeForm({
            name: '',
            email: '',
            role: '',
            salary: '',
            contact: '',
            hire_date: '',
            employee_number: ''
        });
        setMessage('');
    };

    const handleEmployeeFormChange = (e) => {
        const { name, value } = e.target;
        setEmployeeForm((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleSaveEmployee = async () => {
        if (userRole !== 'admin') {
            setMessage('您没有权限保存员工信息。');
            return;
        }
        const { error } = await supabase
            .from('employees')
            .update({
                name: employeeForm.name,
                email: employeeForm.email,
                role: employeeForm.role,
                salary: employeeForm.salary,
                contact: employeeForm.contact,
                hire_date: employeeForm.hire_date,
                employee_number: employeeForm.employee_number
            })
            .eq('id', editingEmployeeId);

        if (!error) {
            setMessage('员工信息更新成功！');
            handleCancelEdit();
            await fetchEmployees();
        } else {
            setMessage('更新员工信息时出错。');
            console.error(error);
        }
    };

    const handleDeleteEmployee = async (employeeId) => {
        if (userRole !== 'admin') {
            setMessage('您没有权限删除员工。');
            return;
        }
        const { error } = await supabase.from('employees').delete().eq('id', employeeId);
        if (!error) {
            setMessage('员工删除成功！');
            await fetchEmployees();
        } else {
            setMessage('删除员工时出错。');
            console.error(error);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <SidebarNav />

            <div className="lg:pl-64">
                <div className="max-w-4xl mx-auto px-4 py-8">
                    {/* Header */}
                    <header className="flex items-center justify-between mb-6">
                        <h3 className="text-2xl font-semibold text-gray-900">员工信息</h3>
                    </header>

                    {/* Message Display */}
                    {message && (
                        <div
                            className={`mb-6 p-4 rounded-md ${
                                message.includes('成功') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                            }`}
                        >
                            <p className="text-sm">{message}</p>
                        </div>
                    )}

                    {/* Employee Table */}
                    <div className="overflow-x-auto bg-white shadow-sm rounded-lg border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">姓名</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">邮箱</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">职位</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">工资</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">联系方式</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">入职日期</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">员工编号</th>
                                {userRole === 'admin' && (
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                                )}
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {employees.map((employee) => (
                                <tr key={employee.id} className="hover:bg-gray-50">
                                    {editingEmployeeId === employee.id ? (
                                        <>
                                            <td className="px-4 py-2">
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={employeeForm.name}
                                                    onChange={handleEmployeeFormChange}
                                                    placeholder="姓名"
                                                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                />
                                            </td>
                                            <td className="px-4 py-2">
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={employeeForm.email}
                                                    onChange={handleEmployeeFormChange}
                                                    placeholder="邮箱"
                                                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                />
                                            </td>
                                            <td className="px-4 py-2">
                                                <input
                                                    type="text"
                                                    name="role"
                                                    value={employeeForm.role}
                                                    onChange={handleEmployeeFormChange}
                                                    placeholder="职位"
                                                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                />
                                            </td>
                                            <td className="px-4 py-2">
                                                <input
                                                    type="number"
                                                    name="salary"
                                                    value={employeeForm.salary}
                                                    onChange={handleEmployeeFormChange}
                                                    placeholder="工资"
                                                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                />
                                            </td>
                                            <td className="px-4 py-2">
                                                <input
                                                    type="text"
                                                    name="contact"
                                                    value={employeeForm.contact}
                                                    onChange={handleEmployeeFormChange}
                                                    placeholder="联系方式"
                                                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                />
                                            </td>
                                            <td className="px-4 py-2">
                                                <input
                                                    type="date"
                                                    name="hire_date"
                                                    value={employeeForm.hire_date}
                                                    onChange={handleEmployeeFormChange}
                                                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                />
                                            </td>
                                            <td className="px-4 py-2">
                                                <input
                                                    type="text"
                                                    name="employee_number"
                                                    value={employeeForm.employee_number}
                                                    onChange={handleEmployeeFormChange}
                                                    placeholder="员工编号"
                                                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                />
                                            </td>
                                            <td className="px-4 py-2 space-x-2">
                                                <button onClick={handleSaveEmployee} className="text-blue-600 hover:underline">保存</button>
                                                <button onClick={handleCancelEdit} className="text-red-600 hover:underline">取消</button>
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td className="px-4 py-2">{employee.name}</td>
                                            <td className="px-4 py-2">{employee.email}</td>
                                            <td className="px-4 py-2">{employee.role}</td>
                                            <td className="px-4 py-2">R {employee.salary}</td>
                                            <td className="px-4 py-2">{employee.contact}</td>
                                            <td className="px-4 py-2">{employee.hire_date ? employee.hire_date.slice(0, 10) : ''}</td>
                                            <td className="px-4 py-2">{employee.employee_number}</td>
                                            {userRole === 'admin' && (
                                                <td className="px-4 py-2 space-x-2">
                                                    <button onClick={() => handleEditEmployee(employee)} className="text-blue-600 hover:underline">编辑</button>
                                                    <button onClick={() => handleDeleteEmployee(employee.id)} className="text-red-600 hover:underline">删除</button>
                                                </td>
                                            )}
                                        </>
                                    )}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

    export default EmployeeManagement;